import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiGetStoreById } from 'services/StoreService';
import {  apiGetTicketById, apiGetTicketList, apiPutTicket } from 'services/TicketsService'
import { scroller } from 'utils/storeHube/helpers';
import { RESPONSIBLE_ROLES } from 'constants/tickets.constant';
import { statusResponse } from 'constants/apiResponse.constant';
import { DEFAULT_STATUS_BY_VIEW, getDefaultStatusForView } from 'constants/ticketStatus.constant';
import { buildInboxQuery } from '../utils/buildInboxQuery';

const NEW_TICKET_STATUS = 1
const ASSIGNED_STATUS = 2

export const getTickets = createAsyncThunk('salesProductList/data/getProducts', async (data) => {
    //El {data} trae la query
    const response = await apiGetTicketList(data)
    return response.data
})

export const getTicketById = createAsyncThunk('salesProductList/data/getProductsById', async (data) => {
    const response = await apiGetTicketById(data)
    return response.data
})

// Relee el ticket abierto sin lo que hace `getTicketById` al cargarlo: el esqueleto
// (`loadingSelectedTicket`), que vuelve a montar la vista en la pestaña por defecto, y el
// scroll a la actividad. Lo usa el resultado de la IA, que termina mientras el usuario mira
// las respuestas: con una recarga por foto, la pantalla saltaba de pestaña en cada una.
// También lo escucha el slice de Mis tareas.
export const refreshSelectedTicket = createAsyncThunk('salesProductList/data/refreshSelectedTicket', async (id) => {
    const response = await apiGetTicketById(id)
    return response.data
})

// Guard de cache por storeId. Si el ultimo fetch fue para el mismo store y
// ya tenemos data en memoria, corto-circuita devolviendo null. Evita un
// `apiGetStoreById` redundante cada vez que TicketContent remonta (cada
// switch de ticket abierto). El reducer fulfilled ignora payload null.
// Marca el ticket como "en proceso" cuando el usuario asignado empieza a
// interactuar con el cuestionario (decisión reunión Sabri 2026-05-19: no
// auto-pasar a en proceso solo por abrir el detalle, solo cuando empieza
// a responder).
//
// Guards (en orden):
// - condition: corta antes del fetch si el ticket no aplica (no es el
//   asignado, ya no esta en estado Nuevo, no hay ticketId) o si ya hay
//   un request en vuelo para el mismo ticket (evita doble disparo en
//   navegacion rapida o remount).
// - El reducer pending registra el ticket en pendingMarkInProgress.
// - El reducer fulfilled/rejected lo limpia.
export const markTicketInProgressIfNeeded = createAsyncThunk(
    'ticketList/data/markInProgressIfNeeded',
    async ({ ticketId }) => {
        const { status, data } = await apiPutTicket(ticketId, {
            ticket_status_id: ASSIGNED_STATUS,
        })
        if (status !== statusResponse.SUCCESS) return null
        return data.data
    },
    {
        condition: ({ ticketId, operatorId, currentUserId, currentStatusId }, { getState }) => {
            if (!ticketId) return false
            if (!operatorId || !currentUserId || operatorId !== currentUserId) return false
            if (currentStatusId !== NEW_TICKET_STATUS) return false
            const pending = getState().ticketList?.data?.pendingMarkInProgress
            if (pending && pending[ticketId]) return false
            return true
        },
    }
)

export const getUsersAllowed = createAsyncThunk(
    '/global/users_allowed',
    async (id, { getState }) => {
        const state = getState().ticketList?.data
        if (
            state?.lastFetchedUsersAllowedStoreId === id &&
            Array.isArray(state?.usersAllowed) &&
            state.usersAllowed.length > 0
        ) {
            return null
        }
        const resStores = await apiGetStoreById(id)
        const { props } = resStores.data.data
        const availableRoles = RESPONSIBLE_ROLES.filter(role => {
            const value = props[role]
            return Array.isArray(value) ? value.length > 0 : Boolean(value)
        })
        const responsableMap = {}
        availableRoles.forEach(role => {
            const users = props[role]
            if (Array.isArray(users)) {
                responsableMap[role] = users
            }
        })
        return {
            users_allowed: props.users_allowed,
            available_roles: availableRoles,
            responsable_map: responsableMap,
            storeId: id,
        }
    }
)


// El estado inicial por vista sale del contrato en `constants/ticketStatus.constant`,
// no de literales repetidos acá. Antes este string y el de `resetTicketStore`
// hardcodeaban `1,2,7,8` por su cuenta, así que cambiar qué es "abierto" pedía
// acordarse de tocarlos además de los switch que arman la query.
export const initialStatusByView = { ...DEFAULT_STATUS_BY_VIEW }

export const initialTableData = {
    total: 0,
    pageIndex: 1,
    pageSize: 30,
    pageFrom: 0,
    pageTo: 1,
    query: '/tickets',
    queryInbox: buildInboxQuery({
        view: 'operator',
        statusGroups: getDefaultStatusForView('operator'),
    }),
    filterSelected: {
        name: 'operator',
        value: ''
    },
    sort: {
        order: '',
        key: '',
    }
}

const dataSlice = createSlice({
    name: 'ticketList/data',
    initialState: {

        tableData: initialTableData,

        ticketList: [],
        selectedTicket: {},
        selectedTicketId: '',
        usersAllowed: [],
        availableRoles: [],
        responsableMap: {},
        lastFetchedUsersAllowedStoreId: null,
        // Cache de questionGroup por ticket_category_secondary.id. Se llena
        // cuando TicketContent fetchea via apiGetTicketsCategoriesById, y
        // permite que al volver a un ticket de la misma categoria el
        // cuestionario se renderice instantaneamente (sin skeleton).
        questionGroupCache: {},
        // Tickets con request markTicketInProgressIfNeeded en vuelo.
        // Map ticketId -> true. Permite dedupe global cuando el usuario
        // navega rapido o el componente remonta antes de que resuelva
        // el PUT.
        pendingMarkInProgress: {},
        storeSelected: null,
        parentCategorySelected: null,
        categorySelected: [],
        // Estado elegido POR VISTA, no un valor global. Con uno solo compartido,
        // elegir "Canceladas" en "Todo" y pasar a "Creado por mí" arrancaba esa
        // vista en canceladas, pisando su default; y resetearlo al cambiar hacía
        // que "Todo" perdiera la selección al volver.
        statusByView: { ...DEFAULT_STATUS_BY_VIEW },

        loading: false,
        latestRequestId: null,
        loadingSelectedTicket: false,
        loadingUsersAllowed: true
    },
    reducers: {
        updateTicketList: (state, action) => {
            state.ticketList = action.payload
        },
        setTableData: (state, action) => {
            state.tableData = action.payload
        },
        setFilterData: (state, action) => {
            state.filterData = action.payload
        },
        setSelectedTicket: (state, action) => {
            state.selectedTicket = action.payload
        },
        setSelectedTicketId: (state, action) => {
            state.selectedTicketId = action.payload
        },
        setLoadingSate: (state, action) => {
            state.loading = action.payload
        },
        resetTicketStore: (state, action) => {
            const userId = action.payload
            // Solo corre al cambiar de `pathname`, o sea al entrar al Inbox desde
            // otra pantalla. Ir y volver del detalle no lo dispara (el detalle es
            // `?ticket=`, mismo pathname), así que el estado elegido sobrevive esa
            // ida y vuelta, que es lo que se quería.
            state.statusByView = { ...DEFAULT_STATUS_BY_VIEW }

            if (userId) {
                state.tableData = {
                    ...initialTableData,
                    queryInbox: buildInboxQuery({
                        view: 'operator',
                        userId,
                        statusGroups: getDefaultStatusForView('operator'),
                    }),
                }
            } else {
                state.tableData = initialTableData
            }
        },
        setStatusForView: (state, action) => {
            const { view, statusGroups } = action.payload || {}
            if (!view || !Array.isArray(statusGroups)) return
            state.statusByView[view] = statusGroups
        },
        resetFilterSelected: (state, action) => {
            state.tableData.filterSelected = initialTableData.filterSelected
        }, 
        setSelectedStore: (state, action) => {
            state.storeSelected = action.payload
        },
        setSelectedParentCategory: (state, action) => {
            state.parentCategorySelected = action.payload
        },
        setSelectedCategory: (state, action) => {
            state.categorySelected = action.payload
        },
        setQuestionGroupCache: (state, action) => {
            const { categoryId, group_id, group } = action.payload || {}
            if (!categoryId) return
            state.questionGroupCache[categoryId] = { group_id, group }
        }
    },
    extraReducers: {
        [getTickets.fulfilled]: (state, action) => {
            // Ignorar respuestas stale (de requests anteriores al actual)
            if (state.latestRequestId && action.meta.requestId !== state.latestRequestId) {
                return
            }

            const meta = action.payload.meta

            if (meta) {
                state.tableData.total = meta.total
                state.tableData.pageIndex = meta.current_page
                state.tableData.pageSize = meta.per_page
                state.tableData.pageFrom = meta.from
                state.tableData.pageTo = meta.to
            }

            state.ticketList = action.payload.data
            state.loading = false
        },
        [getTicketById.fulfilled]: (state, action) => {
            const isNewSelected = action.payload.data.id === state.selectedTicketId
            // Evita race condition: si el usuario clickea A->B rapido y A responde
            // despues de B, ignoramos la respuesta stale de A (no piso selectedTicket
            // ni apago el loading que B sigue esperando).
            if (!isNewSelected) return
            state.selectedTicket = action.payload.data
            state.loadingSelectedTicket = false
            scroller('replies')
        },
        [getTicketById.pending]: (state) => {
            state.loadingSelectedTicket = true
        },
        [refreshSelectedTicket.fulfilled]: (state, action) => {
            // Si el usuario ya abrió otro ticket, no se lo piso.
            if (action.payload.data.id !== state.selectedTicketId) return
            state.selectedTicket = action.payload.data
        },
        [getTickets.pending]: (state, action) => {
            state.loading = true
            state.latestRequestId = action.meta.requestId
        },

        [getUsersAllowed.pending]: (state, action) => {
            // Si el pending corresponde al mismo store ya cacheado, NO
            // wipear: el thunk va a corto-circuitar en fulfilled y queremos
            // mantener visible la data. Solo wipear si cambia el storeId.
            const id = action.meta.arg
            if (
                state.lastFetchedUsersAllowedStoreId === id &&
                Array.isArray(state.usersAllowed) &&
                state.usersAllowed.length > 0
            ) {
                return
            }
            state.usersAllowed = []
            state.availableRoles = []
            state.responsableMap = {}
            state.loadingUsersAllowed = true
        },
        [getUsersAllowed.rejected]: (state) => {
            state.loadingUsersAllowed = false
        },
        [markTicketInProgressIfNeeded.pending]: (state, action) => {
            const ticketId = action.meta.arg?.ticketId
            if (ticketId) state.pendingMarkInProgress[ticketId] = true
        },
        [markTicketInProgressIfNeeded.fulfilled]: (state, action) => {
            const ticketId = action.meta.arg?.ticketId
            if (ticketId) delete state.pendingMarkInProgress[ticketId]
            const updated = action.payload
            if (!updated) return
            if (state.selectedTicket?.id === updated.id) {
                state.selectedTicket = updated
            }
            const idx = state.ticketList.findIndex(t => t.id === updated.id)
            if (idx !== -1) {
                state.ticketList[idx] = updated
            }
        },
        [markTicketInProgressIfNeeded.rejected]: (state, action) => {
            const ticketId = action.meta.arg?.ticketId
            if (ticketId) delete state.pendingMarkInProgress[ticketId]
        },
        [getUsersAllowed.fulfilled]: (state, action) => {
            // Cache hit: el thunk corto-circuito devolviendo null. No
            // pisar usersAllowed/availableRoles/responsableMap (ya estan
            // bien) y solo apago el loading.
            if (!action.payload) {
                state.loadingUsersAllowed = false
                return
            }
            state.usersAllowed = action.payload.users_allowed
            state.availableRoles = action.payload.available_roles
            state.responsableMap = action.payload.responsable_map
            state.lastFetchedUsersAllowedStoreId = action.payload.storeId
            state.loadingUsersAllowed = false
        },
    }
})

export const { 

    updateTicketList,
    setTableData,
    setFilterData,
    setSortedColumn,
    setSelectedTicket,
    setLoadingSate,
    setSelectedTicketId,
    resetTicketStore,
    resetFilterSelected, 
    setSelectedStore,
    setSelectedParentCategory,
    setSelectedCategory,
    setQuestionGroupCache,
    setStatusForView


} = dataSlice.actions

export default dataSlice.reducer
